import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Link as LinkIcon, 
  BarChart3, 
  FileText, 
  Megaphone, 
  Settings, 
  LogOut,
  Moon,
  Sun,
  Brain,
  Zap,
  ClipboardList,
  Lock,
  Shield,
  TrendingUp,
  Building2,
  Tags,
  Link2,
  Users,
  Crown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/components/ThemeProvider';
import { useBeta } from '@/components/BetaProvider';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const { betaMode } = useBeta();
  const { user, isSuperAdmin } = useAuth();
  
  // Check if user has super_admin role using auth context helper
  const isAdmin = isSuperAdmin();

  const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Overview', testId: 'nav-dashboard' },
    { path: '/ai-chat', icon: Brain, label: 'AI Assistant', testId: 'nav-ai-chat', highlight: true },
    { path: '/ai-actions', icon: Zap, label: 'AI Actions', testId: 'nav-ai-actions' },
    { path: '/intelligence', icon: TrendingUp, label: 'Market Intelligence', testId: 'nav-intelligence', isNew: true },
    { path: '/taxonomy', icon: Tags, label: 'Taxonomy Builder', testId: 'nav-taxonomy', isNew: true },
    { path: '/utm-builder', icon: Link2, label: 'UTM Builder', testId: 'nav-utm', isNew: true },
    { path: '/platforms', icon: LinkIcon, label: 'Platforms', testId: 'nav-platforms' },
    { path: '/reports', icon: BarChart3, label: 'Reports', testId: 'nav-reports' },
    { path: '/audit', icon: FileText, label: 'Audit', testId: 'nav-audit' },
    { path: '/campaigns', icon: Megaphone, label: 'Campaigns', testId: 'nav-campaigns' },
    { path: '/onboarding', icon: ClipboardList, label: 'Onboarding', testId: 'nav-onboarding' },
    { path: '/settings', icon: Settings, label: 'Settings', testId: 'nav-settings' },
  ];
  
  // Admin menu items (visible only to super_admin)
  const adminMenuItems = isAdmin ? [
    { path: '/users', icon: Users, label: 'User Management', testId: 'nav-users', adminOnly: true, isNew: true },
    { path: '/governance', icon: Shield, label: 'Governance Console', testId: 'nav-governance', adminOnly: true },
    { path: '/admin', icon: Crown, label: 'Super Admin', testId: 'nav-admin', adminOnly: true },
  ] : [];

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success('Session ended securely');
      // Redirect to MediaKshetra with logout message
      navigate('/logout');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/logout');
    }
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside className="w-64 border-r border-border bg-card flex flex-col">
        {/* Logo & GIOS Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center space-x-2 mb-1">
            <div className="w-7 h-7 bg-primary rounded flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="text-lg font-semibold tracking-tight">MediaKshetra</span>
            <span className="text-[10px] tracking-widest text-primary/70">GIOS</span>
          </div>
          <p className="text-[10px] text-muted-foreground tracking-wide">
            Growth Infrastructure Operating System
          </p>
          {betaMode && (
            <div className="mt-3 flex items-center gap-1.5">
              <span className="inline-flex items-center gap-1 text-[9px] px-2 py-0.5 rounded tracking-wider uppercase"
                    style={{ backgroundColor: 'rgba(212, 168, 75, 0.1)', color: '#B8975F', border: '1px solid rgba(212, 168, 75, 0.15)' }}>
                <Lock className="w-2.5 h-2.5" />
                Private Beta Access
              </span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={item.testId}
                className={`flex items-center space-x-3 px-4 py-2.5 rounded-md transition-colors text-sm ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : item.highlight
                    ? 'text-primary hover:bg-primary/10'
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                }`}
              >
                <item.icon className={`w-4 h-4 ${item.highlight && !isActive ? 'text-primary' : ''}`} />
                <span>{item.label}</span>
                {item.highlight && !isActive && (
                  <span className="ml-auto text-[9px] bg-primary/20 text-primary px-1.5 py-0.5 rounded tracking-wide">NEW</span>
                )}
                {item.isNew && !isActive && (
                  <span className="ml-auto text-[9px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded tracking-wide">GOIS</span>
                )}
              </Link>
            );
          })}
          
          {/* Admin Section */}
          {adminMenuItems.length > 0 && (
            <>
              <div className="pt-4 pb-2">
                <p className="px-4 text-[10px] uppercase tracking-wider text-muted-foreground/60">MediaKshetra Admin</p>
              </div>
              {adminMenuItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={item.testId}
                    className={`flex items-center space-x-3 px-4 py-2.5 rounded-md transition-colors text-sm ${
                      isActive
                        ? 'bg-amber-500/20 text-amber-400'
                        : 'text-amber-500/70 hover:bg-amber-500/10 hover:text-amber-400'
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        {/* Bottom Actions */}
        <div className="p-4 border-t border-border space-y-1">
          <Button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            variant="ghost"
            size="sm"
            className="w-full justify-start text-xs"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4 mr-3" /> : <Moon className="w-4 h-4 mr-3" />}
            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </Button>
          <Button
            onClick={handleLogout}
            data-testid="logout-button"
            variant="ghost"
            size="sm"
            className="w-full justify-start text-xs text-muted-foreground hover:text-foreground"
          >
            <LogOut className="w-4 h-4 mr-3" />
            Exit Environment
          </Button>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-1.5 text-[9px] text-muted-foreground/50">
            <Shield className="w-3 h-3" />
            <span>Secure Institutional Environment</span>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
};

export default DashboardLayout;
